import { z } from 'zod';
import { uuidSchema } from '../common';
import type { OrgCredentialResponse } from './organization-credentials';

// 9 Cloudflare DurableObject location hints. `sam`, `afr`, `me` currently route
// to the nearest supported region (CF doesn't yet spawn DOs there) — see
// https://developers.cloudflare.com/durable-objects/reference/data-location/
// The customer-stored value takes effect natively once CF expands support.
export const primaryRegionSchema = z.enum([
  'wnam', 'enam', 'sam', 'weur', 'eeur', 'apac', 'oc', 'afr', 'me',
]);
export type PrimaryRegion = z.infer<typeof primaryRegionSchema>;

// Source of the placement_region value resolved by the backend cascade. `unpinned` (hubs only):
// the hub's DO was placed with no location hint, so placement_region is null — a production hub a
// publish placed before publish carried its source's hint (wayai#5032), recorded at its next write,
// or a hub published or replicated since from a source recording no region. A preview replicated
// before wayai#5032 keeps a null source. A hint is best effort, honored only at the object's creation.
export const placementSourceSchema = z.enum([
  'hub_config', 'org_config', 'parent_cascade',
  'cf_colo', 'country', 'fallback', 'unpinned',
]);
export type PlacementSource = z.infer<typeof placementSourceSchema>;

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const orgIdParam = z.object({
  id: uuidSchema,
});

export const orgAdminIdParam = z.object({
  id: uuidSchema,
  adminId: z.string().min(1),
});

export const createOrganizationBody = z.object({
  organization_name: z.string().min(1, 'organization_name is required'),
  // Write-once at creation; updateOrganizationBody does not accept this.
  primary_region: primaryRegionSchema.optional(),
});

export const updateOrganizationBody = z.object({
  organization_name: z.string().min(1).optional(),
  organization_avatar_url: z.string().url().nullable().optional(),
  timezone: z.string().optional(),
  business_hours: z.unknown().optional(),
}).passthrough();

export const addOrgAdminBody = z.object({
  user_email: z.string().email('valid email is required'),
});

// =============================================================================
// INFERRED TYPES (for consumers that need request types without importing zod)
// =============================================================================

export type CreateOrganizationInput = z.infer<typeof createOrganizationBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface OrganizationResponse {
  organization_id: string;
  created_at: string;
  organization_name: string;
  updated_at: string;
  created_by: string;
  billing_currency: string;
  primary_region: PrimaryRegion | null;
  placement_region: PrimaryRegion | null;
  placement_source: PlacementSource | null;
}

export interface OrganizationAdminUserResponse {
  organization_admin_user_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  enabled: boolean;
  user_email: string;
  is_owner: boolean;
  /**
   * The person's id in the org (wayai-pro/wayai#5473 §3, `prs_…`): what a record's person fields hold for
   * them. Present only for an org admin of the org, and only on a row naming an account.
   */
  person_id?: string;
}

export interface GetOrganizationResponse {
  data: {
    organization: OrganizationResponse;
    admin_users: OrganizationAdminUserResponse[];
  };
}

export interface CreateOrganizationResponse {
  data: OrganizationResponse[];
}

export interface UpdateOrganizationResponse {
  data: OrganizationResponse[];
}

export interface AddOrgAdminResponse {
  data: OrganizationAdminUserResponse[];
}

// Composite org-view endpoint — returns everything OrganizationDetailView needs
// (organization + admins + tags + credentials) in one request to collapse the
// parallel fetches that would otherwise fire on every org-settings entry.
// `credentials` is the secret-safe `OrgCredentialResponse` DTO (no `metadata` /
// `encrypted_*` fields) — the composite MUST NOT expose credential secrets.
export interface GetOrganizationViewResponse {
  data: {
    organization: OrganizationResponse;
    admin_users: OrganizationAdminUserResponse[];
    tags: Array<{
      tag_id: string;
      name: string;
      display_name: string | null;
      color: string | null;
      organization_id: string;
      created_at: string;
      updated_at: string;
    }>;
    credentials: OrgCredentialResponse[];
  };
}
